import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Inbox, Loader2, Phone, Mail, Calendar, ExternalLink, Copy,
  CheckCircle2, AlertTriangle, Car, Send, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LeadsLeaTab from "@/components/LeadsLeaTab";
import WidgetLeaTab from "@/components/WidgetLeaTab";

const TYPES = {
  service: "Service / entretien",
  pneus: "Pneus",
  carrosserie: "Carrosserie",
  diagnostic: "Diagnostic / panne",
  controle: "Contrôle officiel",
  autre: "Autre",
};

function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Historique des prises de RDV automatisées via le widget public.
 *
 * Depuis la mise en place du widget 100% automatique :
 *   - Toutes les demandes sont traitées automatiquement (statut = traite_auto)
 *   - Le client est matché ou créé, le RDV planifié, le SMS envoyé
 *   - Aucune action manuelle n'est requise
 *
 * Cette page sert d'**historique consultable** + **embed code** + alertes
 * éventuelles (véhicule rattaché à un autre client, remplacement non réservé…).
 */
export default function DemandesRdv() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smsTel, setSmsTel] = useState("");
  const [smsMessage, setSmsMessage] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsHistory, setSmsHistory] = useState([]);

  const widgetUrl = `${window.location.origin}/widget/rdv`;
  const defaultSmsMessage = `Bonjour, voici le lien pour prendre rendez-vous chez CarMaintenance :\n${widgetUrl}`;

  useEffect(() => {
    if (!smsMessage) setSmsMessage(defaultSmsMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: hist }] = await Promise.all([
        api.get("/rdv-demandes"),
        api.get("/widget-rdv/sms-historique?limit=10").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(data.items || []);
      setSmsHistory(hist.items || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const sendWidgetSms = async () => {
    const tel = smsTel.trim();
    if (!tel) {
      toast.error("Saisissez un numéro");
      return;
    }
    setSmsSending(true);
    try {
      const payload = { tel };
      // N'envoie le message custom QUE s'il diffère du défaut (sinon le backend ajoute le lien)
      const trimmed = smsMessage.trim();
      if (trimmed && trimmed !== defaultSmsMessage.trim()) {
        payload.message = trimmed;
      }
      const { data } = await api.post("/widget-rdv/send-sms", payload);
      const clientInfo = data.client_matched ? ` · client ${data.client_label}` : " · numéro libre";
      const statusInfo = data.sms_status === "sent" ? "envoyé" : (data.sms_status === "mocked" ? "envoyé (mode test)" : `statut ${data.sms_status}`);
      toast.success(`SMS ${statusInfo}${clientInfo}`);
      setSmsTel("");
      setSmsMessage(defaultSmsMessage); // reset au défaut
      const { data: hist } = await api.get("/widget-rdv/sms-historique?limit=10");
      setSmsHistory(hist.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur envoi SMS");
    } finally {
      setSmsSending(false);
    }
  };

  const embedCode = `<iframe src="${widgetUrl}" width="100%" height="900" frameborder="0" style="border:0;border-radius:12px;"></iframe>`;
  const copyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    toast.success("Code d'intégration copié");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="demandes-rdv-page">
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2 mb-1">
        <Inbox className="w-7 h-7 text-[#52a675]" /> Widget de prise de RDV
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Prises de RDV reçues depuis car-main.ch. <b>100% automatique</b> : client matché ou créé, RDV planifié, SMS de confirmation envoyé.
      </p>

      <Tabs defaultValue="rdv">
        <TabsList className="mb-4">
          <TabsTrigger value="rdv" data-testid="tab-prises-rdv">Prises de RDV</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads-lea">🤖 Leads Léa</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-widget-config">⚙️ Catégories Léa</TabsTrigger>
        </TabsList>

        <TabsContent value="leads">
          <LeadsLeaTab />
        </TabsContent>

        <TabsContent value="config">
          <WidgetLeaTab />
        </TabsContent>

        <TabsContent value="rdv">
      {/* Embed code */}
      <Card className="p-4 mb-4 bg-gradient-to-br from-[#52a675]/5 to-transparent border-[#52a675]/30">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-[#2d5f3f] font-bold mb-1">🌐 Intégration sur votre site</div>
            <div className="text-sm text-gray-700 mb-2">Copiez ce code HTML dans n'importe quelle page de car-main.ch :</div>
            <code className="block bg-white border border-gray-200 rounded px-3 py-2 text-[10px] sm:text-xs font-mono text-gray-800 break-all">{embedCode}</code>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={copyEmbed} data-testid="copy-embed-btn"><Copy className="w-3.5 h-3.5 mr-1.5" /> Copier</Button>
            <a href={widgetUrl} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Voir le widget</Button>
            </a>
          </div>
        </div>
      </Card>

      {/* Envoi SMS lien widget (manuel) */}
      <Card className="p-4 mb-4 bg-gradient-to-br from-blue-50 to-transparent border-blue-200" data-testid="widget-sms-card">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-blue-800 font-bold mb-1 flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Envoyer le lien par SMS
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Un client appelle pour prendre RDV ? Envoyez-lui le lien du widget directement par SMS pour qu'il choisisse son créneau.
            </p>
            <div className="flex gap-2 flex-wrap items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-gray-500 mb-1 block">Numéro de téléphone</label>
                <Input
                  type="tel"
                  inputMode="tel"
                  placeholder="+41 79 123 45 67"
                  value={smsTel}
                  onChange={(e) => setSmsTel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !smsSending) sendWidgetSms(); }}
                  data-testid="widget-sms-tel-input"
                />
              </div>
              <Button
                onClick={sendWidgetSms}
                disabled={smsSending || !smsTel.trim()}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="widget-sms-send-btn"
              >
                {smsSending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Envoyer le lien
              </Button>
            </div>
            {/* Message personnalisable */}
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-gray-500">Message envoyé</label>
                <button
                  type="button"
                  onClick={() => setSmsMessage(defaultSmsMessage)}
                  className="text-[10px] text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                  disabled={smsMessage.trim() === defaultSmsMessage.trim()}
                  data-testid="widget-sms-reset-btn"
                >
                  Réinitialiser
                </button>
              </div>
              <Textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                rows={3}
                className="text-xs font-mono bg-white"
                placeholder={defaultSmsMessage}
                data-testid="widget-sms-message-textarea"
              />
              <div className="text-[10px] text-gray-400 mt-1">
                {smsMessage.length} caractères · {smsMessage.length > 160 ? <span className="text-amber-600 font-semibold">peut être facturé en plusieurs SMS</span> : "1 SMS standard"}
              </div>
            </div>
            {smsHistory.length > 0 && (
              <details className="mt-3 text-xs" data-testid="widget-sms-history">
                <summary className="cursor-pointer text-blue-700 hover:text-blue-900 font-medium">
                  Historique récent ({smsHistory.length})
                </summary>
                <ul className="mt-2 space-y-1 text-gray-600">
                  {smsHistory.map((h) => (
                    <li key={h.id} className="flex items-center gap-2 py-0.5">
                      <span className="font-mono text-gray-800">{h.tel}</span>
                      {h.client_label && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700">{h.client_label}</Badge>}
                      <span className="text-gray-400">·</span>
                      <span>{fmtDateTime(h.created_at)}</span>
                      <span className="text-gray-400">· par {h.envoye_par_nom}</span>
                      {h.sms_status !== "sent" && h.sms_status !== "mocked" && (
                        <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700">{h.sms_status}</Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>
      </Card>

      <div className="text-xs text-gray-500 mb-3 px-1">
        Historique des prises de RDV via widget ({items.length})
      </div>

      {loading && <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
      {!loading && items.length === 0 && (
        <Card className="p-12 text-center text-gray-400 border-gray-200">Aucune prise de RDV via le widget pour l'instant.</Card>
      )}
      <div className="space-y-2">
        {items.map((d) => {
          const hasAlerte = d.remplacement_alerte || d.vehicule_alerte;
          return (
            <Card key={d.id} className="p-4 border-gray-200" data-testid={`demande-${d.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Traité automatiquement
                    </Badge>
                    {hasAlerte && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> À vérifier
                      </Badge>
                    )}
                    <span className="text-sm font-semibold text-gray-900">{d.prenom} {d.nom}</span>
                    <span className="text-xs text-gray-400">· {fmtDateTime(d.created_at)}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 text-xs text-gray-700 mt-2">
                    <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-gray-400" /> <strong>{fmtDate(d.date_souhaitee)}</strong> ({d.creneau_souhaite === "matin" ? "matin" : "après-midi"})</div>
                    <div className="flex items-center gap-1.5">🔧 <strong>{d.type_intervention_label || TYPES[d.type_service] || d.type_service}</strong></div>
                    <a href={`tel:${d.telephone}`} className="flex items-center gap-1.5 hover:text-[#52a675]"><Phone className="w-3.5 h-3.5 text-gray-400" /> {d.telephone}</a>
                    {d.email && <a href={`mailto:${d.email}`} className="flex items-center gap-1.5 hover:text-[#52a675]"><Mail className="w-3.5 h-3.5 text-gray-400" /> {d.email}</a>}
                  </div>
                  {(d.marque || d.modele || d.plaque) && (
                    <div className="text-xs text-gray-600 mt-1 flex items-center gap-1.5">
                      <Car className="w-3.5 h-3.5 text-gray-400" /> {[d.marque, d.modele, d.plaque].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  {d.vehicule_remplacement_souhaite && (
                    <div className="text-xs text-gray-600 mt-1">
                      🚙 Véhicule de remplacement demandé
                      {d.reservation_remplacement_id
                        ? <span className="text-emerald-700"> · réservé ✓</span>
                        : <span className="text-amber-700"> · à confirmer manuellement</span>}
                    </div>
                  )}
                  {d.message && (
                    <div className="text-xs text-gray-600 mt-2 italic bg-gray-50 p-2 rounded whitespace-pre-wrap">{d.message}</div>
                  )}
                </div>
                {d.rdv_id && (
                  <a
                    href={`/calendrier?rdv=${d.rdv_id}`}
                    className="text-xs text-[#52a675] hover:underline flex-shrink-0 mt-1"
                    data-testid={`demande-${d.id}-voir-rdv`}
                  >
                    Voir le RDV →
                  </a>
                )}
              </div>
            </Card>
          );
        })}
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
